import * as React from "react";

import { useMediaQuery, useTheme } from "@mui/material";

import { FormTemplateDesktop } from "./FormTemplateDesktop";
import { FormTemplateMobile } from "./FormTemplateMobile";
import { identity } from "lodash";
import { FormMenuEntry } from "@/types/index";
import { Help } from "@/components/atoms/HelpInfo";

export type FormTemplateProps = {
  menuEntries: FormMenuEntry[];
  busy?: boolean;
  currentSection?: string;
  error?: string;
  nextLabel?: string;
  saveLabel?: string | null;
  children: React.ReactNode;
  onNext?: () => void;
  onSave?: () => void;
  onSectionClick: (section: string) => void;
};

export const FormTemplate: React.FC<FormTemplateProps> = ({
  menuEntries,
  busy,
  currentSection,
  error,
  children,
  nextLabel,
  saveLabel,
  onNext,
  onSave,
  onSectionClick,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  if (matches) {
    return (
      <FormTemplateMobile
        menuEntries={menuEntries}
        busy={busy}
        currentSection={currentSection}
        error={error}
        nextLabel={nextLabel}
        saveLabel={saveLabel}
        onNext={onNext}
        onSave={onSave}
        onSectionClick={onSectionClick}
      >
        {children}
        <br></br>
        <Help />
      </FormTemplateMobile>
    );
  }

  return (
    <FormTemplateDesktop
      menuEntries={menuEntries}
      busy={busy}
      currentSection={currentSection}
      error={error}
      nextLabel={nextLabel}
      saveLabel={saveLabel}
      onNext={onNext}
      onSave={onSave}
      onSectionClick={onSectionClick}
    >
      {children}
    </FormTemplateDesktop>
  );
};

type State = {
  busy?: boolean;
  currentSection?: string;
  nextLabel?: string;
  saveLabel?: string | null;
  onNext?: () => void;
  onSave?: () => void;
  onSectionClick?: (section: string) => void;
};

const useFormTemplate = () => {
  const [{ busy, currentSection, nextLabel, saveLabel, onNext, onSave, onSectionClick }, update] =
    React.useState<State>({
      onSectionClick: identity,
    });

  return {
    busy,
    currentSection,
    nextLabel,
    saveLabel,
    onNext,
    onSave,
    onSectionClick,
    update,
  };
};

export type UpdateFormTemplateProc = (state: State) => void;

type WithFormTemplateProps = {
  menuEntries: FormMenuEntry[];
  proc: (update: UpdateFormTemplateProc) => React.ReactNode;
};

export const WithFormTemplate: React.FC<WithFormTemplateProps> = ({ menuEntries, proc }) => {
  const { busy, currentSection, nextLabel, saveLabel, onNext, onSave, onSectionClick, update } =
    useFormTemplate();

  return (
    <FormTemplate
      menuEntries={menuEntries}
      busy={busy}
      currentSection={currentSection}
      nextLabel={nextLabel}
      saveLabel={saveLabel}
      onNext={onNext}
      onSave={onSave}
      onSectionClick={onSectionClick || identity}
    >
      {proc(update)}
    </FormTemplate>
  );
};
